// Phase 18 · Community · Profile page
// Phase 22b · Badge shelf (gamification)
import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Users, MessageCircle, Award, ShieldCheck, Sparkles, Ban } from "lucide-react";
import PostCard from "../../components/community/PostCard";
import BlockUserModal from "../../components/community_v2/BlockUserModal";
import { useAuth } from "../../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProfilePage() {
    const { username } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [prof, setProf] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [showBlock, setShowBlock] = useState(false);
    // Phase 22b · badges
    const [badges, setBadges] = useState([]);
    const [badgesEnabled, setBadgesEnabled] = useState(false);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const r = await fetch(`${API}/api/community/profile/${username}`, { credentials: "include" });
                if (r.status === 404) { setNotFound(true); return; }
                if (r.ok) {
                    const data = await r.json();
                    setProf(data);
                    // Phase 22b · load badges (kill-switch aware)
                    if (data?.user_id) {
                        try {
                            const br = await fetch(`${API}/api/gamification/users/${data.user_id}/badges`, { credentials: "include" });
                            if (br.ok) {
                                const bd = await br.json();
                                setBadges(bd.items || bd.badges || []);
                                setBadgesEnabled(true);
                            }
                        } catch {}
                    }
                }
            } catch {}
            setLoading(false);
        })();
    }, [username]);

    const isSelf = user && (user.username === username || prof?.user_id === user.id);

    if (loading) return (
        <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
            <p className="text-neutral-500 text-sm">Loading…</p>
        </div>
    );
    if (notFound || !prof) return (
        <div className="min-h-screen bg-[#0A0A0A] text-neutral-200 flex flex-col items-center justify-center gap-3">
            <p>@{username} not found</p>
            <Link to="/community" className="text-[#FFFFFF] hover:text-[#CCCCCC] text-sm">← Back to community</Link>
        </div>
    );

    return (
        <div
            className="min-h-screen bg-[#0A0A0A] text-[#FFFFFF] font-inter"
            data-testid="profile-page"
        >
            <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0A0A0A]/80 border-b border-[#1A1A1A]">
                <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
                    <Link to="/community" className="text-neutral-400 hover:text-neutral-200 flex items-center gap-1 text-sm">
                        <ArrowLeft size={16} /> Back
                    </Link>
                    <span className="text-neutral-600">·</span>
                    <a href="/" className="text-sm font-bold bg-white bg-clip-text text-transparent">
                        Global Lead Suite
                    </a>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 py-6">
                <div className="bg-[#111111] border border-[#1A1A1A] rounded-2xl p-6 mb-6">
                    <div className="flex items-start gap-4">
                        <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center text-3xl font-bold text-neutral-900 flex-shrink-0">
                            {prof.username?.[0]?.toUpperCase() || "?"}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h1 className="text-2xl font-bold text-neutral-100" data-testid="profile-username">
                                    @{prof.username}
                                </h1>
                                {prof.verified_creator && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#CCCCCC]/10 border border-[#CCCCCC]/30 text-[#CCCCCC] text-xs font-semibold">
                                        <ShieldCheck size={11} /> Verified Creator
                                    </span>
                                )}
                                {prof.ambassador && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FFFFFF]/10 border border-[#FFFFFF]/30 text-[#FFFFFF] text-xs font-semibold">
                                        <Award size={11} /> Ambassador
                                    </span>
                                )}
                            </div>
                            {prof.bio ? (
                                <p className="mt-1.5 text-sm text-neutral-400">{prof.bio}</p>
                            ) : (
                                <p className="mt-1.5 text-sm text-neutral-600 italic">No bio yet</p>
                            )}
                            <div className="mt-3 flex items-center gap-4 text-xs text-neutral-400">
                                <span className="inline-flex items-center gap-1" data-testid="profile-post-count">
                                    <MessageCircle size={12} /> {prof.post_count || 0} posts
                                </span>
                                <span className="inline-flex items-center gap-1" data-testid="profile-follower-count">
                                    <Users size={12} /> {prof.follower_count || 0} followers
                                </span>
                                <span className="inline-flex items-center gap-1">
                                    <Sparkles size={12} /> {prof.following_count || 0} following
                                </span>
                            </div>
                            {user && !isSelf && (
                                <div className="mt-4 flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => navigate(`/messages/${username}`)}
                                        className="inline-flex items-center gap-1 bg-white text-neutral-900 font-semibold px-3 py-1.5 rounded-lg text-xs"
                                        data-testid="profile-message-btn"
                                    >
                                        <MessageCircle size={13} /> Message
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setShowBlock(true)}
                                        className="inline-flex items-center gap-1 border border-[#1A1A1A] hover:border-[#FAFAFA]/40 text-neutral-400 hover:text-[#FAFAFA] px-3 py-1.5 rounded-lg text-xs"
                                        data-testid="profile-block-btn"
                                    >
                                        <Ban size={13} /> Block
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-[#1A1A1A] text-xs">
                        <p className="text-neutral-500 mb-2">Badges</p>
                        {!badgesEnabled ? (
                            <p className="text-neutral-600 italic mt-1" data-testid="profile-badges-stub">
                                Gamification engine is currently disabled by the workspace admin.
                            </p>
                        ) : badges.length === 0 ? (
                            <p className="text-neutral-600 italic mt-1" data-testid="profile-badges-empty">
                                No badges earned yet.
                            </p>
                        ) : (
                            <div className="flex flex-wrap gap-2 mt-1" data-testid="profile-badge-shelf">
                                {badges.map((b) => {
                                    // Backend returns {badge_slug, earned_at, badge:{slug,title,description,icon_key,tier}}.
                                    // Fall back to top-level fields for older schemas.
                                    const meta = b.badge || b;
                                    const badgeName = meta.title || meta.name || meta.slug || b.badge_slug;
                                    const badgeCode = b.badge_slug || meta.slug || b.id;
                                    const badgeDesc = meta.description || badgeName;
                                    const tier = meta.tier;
                                    return (
                                    <div
                                        key={b.id || badgeCode}
                                        title={badgeDesc}
                                        data-testid={`profile-badge-${badgeCode}`}
                                        className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#1A1A1A] bg-gradient-to-br from-[#FFFFFF]/10 to-[#CCCCCC]/10 hover:border-[#FFFFFF]/50 transition-colors"
                                    >
                                        <span className="text-base leading-none">🏆</span>
                                        <span className="text-[11px] font-semibold text-neutral-200">{badgeName}</span>
                                        {tier && (
                                            <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded ${tier === "gold" ? "bg-neutral-700 text-neutral-100" : tier === "silver" ? "bg-neutral-800 text-neutral-300" : "bg-neutral-900 text-neutral-400"}`}>
                                                {tier}
                                            </span>
                                        )}
                                    </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                <section>
                    <h2 className="text-sm font-semibold text-neutral-200 mb-3">Recent posts</h2>
                    {!prof.recent_posts?.length && (
                        <p className="text-sm text-neutral-500 italic">No posts yet</p>
                    )}
                    <div className="space-y-3">
                        {(prof.recent_posts || []).map((p) => (
                            <PostCard key={p.id} post={p} compact />
                        ))}
                    </div>
                </section>
            </main>

            {showBlock && (
                <BlockUserModal
                    username={username}
                    onClose={() => setShowBlock(false)}
                    onBlocked={() => navigate("/community")}
                />
            )}
        </div>
    );
}
